import React from 'react';

interface SentryErrorData {
    error: unknown;
    componentStack: string;
    eventId: string;
    resetError(): void;
}

/**
 * Error fallback component for Sentry ErrorBoundary
 * Displays a user-friendly error message and reset button
 */
const ErrorFallback: React.FC<SentryErrorData> = ({ error, resetError }) => {
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            padding: '2rem',
            backgroundColor: '#f8f9fa',
        }}>
            <div style={{
                maxWidth: '600px',
                textAlign: 'center',
                backgroundColor: 'white',
                padding: '3rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>
                <h1 style={{ color: '#dc3545', marginBottom: '1rem' }}>
                    Oops! Something went wrong
                </h1>
                <p style={{ color: '#6c757d', marginBottom: '2rem', fontSize: '1.1rem' }}>
                    We're sorry for the inconvenience. Our team has been notified and is working to fix the issue.
                </p>
                {import.meta.env.MODE === 'development' && (
                    <div style={{
                        backgroundColor: '#f1f3f5',
                        padding: '1rem',
                        borderRadius: '4px',
                        marginBottom: '2rem',
                        textAlign: 'left',
                    }}>
                        <h3 style={{ marginBottom: '0.5rem', color: '#495057' }}>Error Details (Development Only):</h3>
                        <pre style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: '0.875rem',
                            color: '#dc3545',
                        }}>
                            {errorMessage}
                        </pre>
                    </div>
                )}
                <button
                    onClick={resetError}
                    style={{
                        padding: '0.75rem 2rem',
                        fontSize: '1rem',
                        color: 'white',
                        backgroundColor: '#007bff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0056b3')}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#007bff')}
                >
                    Try Again
                </button>
            </div>
        </div>
    );
};

export default ErrorFallback;
